// NSF membership on the arbiter's screens: which groups check their players against
// NSF's member register, what a check result means, and what "Bruk NSF-data" would
// change on a player. No DOM and no network, so it is tested under node:test
// (tests/nsf-membership.test.js).
//
// The check itself runs on the server, against the register it keeps in the EU
// (functions/lib/nsf-check.js). js/services/nsf-membership.js asks for it and keeps the
// answer; js/ui/nsf-membership-ui.js draws it.

/** The NSF series a group can belong to. */
export const NSF_SERIES = Object.freeze(['ngp', 'bgp']);

/** Who may enter a group that requires membership. */
export const NSF_ELIGIBILITY = Object.freeze({ MEMBERS: 'members', MEMBERS_OR_FIDE: 'members_or_fide' });

/** A group's (or an ungrouped tournament's) series: 'ngp', 'bgp', or '' for any other. */
function seriesOf(src) {
    const s = String((src && src.nsfSeries) || '').toLowerCase();
    return NSF_SERIES.includes(s) ? s : '';
}

/**
 * The rules for one group (null for a tournament without groups):
 *   { required, series: 'ngp' | 'bgp' | 'other' | null, eligibility: 'members' | 'members_or_fide' | null }
 *
 * The turneringsleder decides: "NSF Membership Required" on the group (or on a tournament
 * without groups) requires membership, and the series beside it says on what terms — NGP
 * lets in anyone with a FIDE ID as well, BGP and any other tournament take members only.
 * A tournament with groups decides per group, whatever its own top-level settings say. The
 * server's copy is groupRules in functions/lib/nsf-rules.js; keep the two in step.
 */
export function nsfGroupRules(tournament, groupId) {
    const t = tournament || {};
    const id = groupId === undefined || groupId === null ? null : String(groupId);
    const grouped = Array.isArray(t.groups) && t.groups.length > 0;
    const src = grouped ? t.groups.find(g => g && id !== null && String(g.id) === id) : t;
    if (!src || src.requireNsfMembership !== true) return { required: false, series: null, eligibility: null };
    const series = seriesOf(src) || 'other';
    return {
        required: true,
        series,
        eligibility: series === 'ngp' ? NSF_ELIGIBILITY.MEMBERS_OR_FIDE : NSF_ELIGIBILITY.MEMBERS
    };
}

/**
 * The groups that require NSF membership, as ids — or [null] for a tournament without
 * groups that requires it itself, and [] when nothing is checked. The same rule as
 * checkedRosters() in functions/lib/nsf-check.js.
 */
export function nsfRequiredGroupIds(tournament) {
    const t = tournament || {};
    if (Array.isArray(t.groups) && t.groups.length > 0) {
        return t.groups.filter(g => g && nsfGroupRules(t, g.id).required).map(g => String(g.id));
    }
    return nsfGroupRules(t, null).required ? [null] : [];
}

/** Does this group (null/undefined for a tournament without groups) require membership? */
export function isNsfRequired(tournament, groupId) {
    const ids = nsfRequiredGroupIds(tournament);
    if (ids.length === 1 && ids[0] === null) return true;
    return groupId !== undefined && groupId !== null && ids.includes(String(groupId));
}

/**
 * The NSF series the tournament's groups belong to, in NSF_SERIES order: ['ngp'],
 * ['ngp', 'bgp'] or []. Saved on the tournament document as `nsfSeriesInUse`
 * (js/services/cloud-sync.js), which is how the NSF console finds NGP and BGP tournaments.
 * The server's copy is seriesInUse in functions/lib/nsf-rules.js.
 */
export function nsfSeriesInUse(tournament) {
    const t = tournament || {};
    const ids = Array.isArray(t.groups) && t.groups.length > 0 ? t.groups.filter(Boolean).map(g => g.id) : [null];
    const used = new Set(ids.map(id => nsfGroupRules(t, id).series));
    return NSF_SERIES.filter(s => used.has(s));
}

/** 'NGP' or 'BGP', for showing. */
export function nsfSeriesLabel(series) {
    return series === 'ngp' ? 'NGP' : series === 'bgp' ? 'BGP' : '';
}

/**
 * A check result as a badge: { kind, tone, label, title }. `tone` is a .status-badge
 * class; `label` and `title` are English source strings for tr(), with {year}, {last}
 * and {name} left for the caller to fill.
 *
 * @param {object|null|undefined} result  the server's answer for one person: null when
 *   the check ran but did not include them (added since the roster was last saved)
 * @param {number} changeCount  how many of the member's details differ from the player's
 *   (nsfChanges().length); a certain match with some shows a dot, to say there is
 *   something "Bruk NSF-data" would update
 */
export function nsfBadge(result, changeCount = 0) {
    if (!result) {
        return { kind: 'unchecked', tone: 'completed', label: 'NSF …', title: 'Not checked yet. Click to check now.' };
    }
    // NGP: not a paid-up member, but a FIDE ID lets them play (the server marks it so only
    // in an NGP group, and only for someone who is not a paid-up member).
    if (result.fide) {
        return { kind: 'fide', tone: 'upcoming', label: 'FIDE', title: 'Not a paid NSF member for {year}, but may play in NGP with a FIDE ID. Gets no GP points.' };
    }
    switch (result.status) {
        case 'unique':
            if (result.membership === 'active') {
                if (result.atRegistration) {
                    return { kind: 'member', tone: 'active', label: 'NSF ✓', title: 'Paid NSF member for {year}, found when they registered.' };
                }
                if (result.atSearch) {
                    return { kind: 'member', tone: 'active', label: 'NSF ✓', title: 'Paid NSF member for {year}, picked from NSF\'s register.' };
                }
                return changeCount > 0
                    ? { kind: 'member', tone: 'active', label: 'NSF ✓ •', title: 'Paid NSF member for {year}. Click to use NSF\'s details for this player.' }
                    : { kind: 'member', tone: 'active', label: 'NSF ✓', title: 'Paid NSF member for {year}.' };
            }
            if (result.membership === 'lapsed') {
                return { kind: 'lapsed', tone: 'warn', label: 'NSF {last}', title: 'Paid for {last}, not yet for {year}.' };
            }
            return { kind: 'unknown', tone: 'warn', label: 'NSF ?', title: 'Found, but NSF\'s list gives no membership year.' };
        case 'probable':
            return { kind: 'probable', tone: 'warn', label: 'NSF?', title: 'Probably {name}. Click to confirm.' };
        case 'ambiguous':
            return { kind: 'ambiguous', tone: 'warn', label: 'NSF?', title: 'Several NSF members fit. Click to choose.' };
        case 'conflict':
            return { kind: 'conflict', tone: 'danger', label: 'NSF ID', title: 'The NSF ID belongs to someone else. Click to see.' };
        default:
            return { kind: 'none', tone: 'completed', label: 'No NSF', title: 'Not found among NSF\'s paid members for {year}.' };
    }
}

/**
 * The answer for someone the check has not answered for yet, who was found among NSF's
 * paid members when they registered: the Registration API stamps an entry it matched to
 * one member with the year that member has paid for (`nsfVerifiedYear`, carried onto the
 * player when the entry is imported). Null when there is no stamp. Shaped like a check
 * result, `atRegistration` marking where it came from; a real check result always comes
 * first, being the newer word.
 *
 * @param {object} person  a player or a pending registration
 * @param {number} year    the year the group's membership must be paid for
 */
export function nsfResultFromRegistration(person, year) {
    return paidYearResult(person && person.nsfVerifiedYear, year, 'atRegistration');
}

/**
 * The same for a player picked out of NSF's register in the player search, before a check
 * has seen them: `paidFor` is the year the register showed them paid for
 * (js/services/nsf-membership.js nsfPickedYear). Marked `atSearch`.
 */
export function nsfResultFromSearch(paidFor, year) {
    return paidYearResult(paidFor, year, 'atSearch');
}

function paidYearResult(paidFor, year, from) {
    const paid = parseInt(paidFor, 10);
    if (!Number.isInteger(paid) || paid <= 0) return null;
    return {
        status: 'unique',
        membership: paid >= year ? 'active' : 'lapsed',
        member: { membershipYear: paid },
        [from]: true
    };
}

const text = v => (v === undefined || v === null ? '' : String(v).trim());

// The player's year of birth, from the year itself or from a full date.
function playerYear(p) {
    const year = text(p.birthYear);
    if (year) return year;
    const m = /^(\d{4})-/.exec(text(p.birthDate));
    return m ? m[1] : '';
}

// The tournament types that are FIDE rating lists, and so have a K-factor in NSF's file.
const FIDE_LISTS = Object.freeze(['standard', 'rapid', 'blitz']);

/**
 * Which FIDE list a group with these settings is rated on, for the member's K-factor:
 * 'standard', 'rapid' or 'blitz' — or null for a youth group, which calculates no ratings,
 * and for a group bound to an arbiter's own rating list, which decides its own K-factors
 * (a K on the player would override them). The server's copy is ratedListOf in
 * functions/lib/nsf-registration.js.
 */
export function nsfRatedList(settings) {
    const s = settings || {};
    if (s.ratingListId) return null;
    const type = s.type || 'standard';
    return FIDE_LISTS.includes(type) ? type : null;
}

/** The member's K-factor for that list, as a player's `kFactor`, or '' when there is none to use. */
export function nsfKFactor(member, settings) {
    const list = nsfRatedList(settings);
    const k = list && member && member.kFactors ? member.kFactors[list] : null;
    return k ? String(k) : '';
}

/**
 * What "Bruk NSF-data" would change on a player: [{ field, from, to }], in the order the
 * dialog lists them. Only what the member's details can fill and the player does not
 * already say: the NSF-ID, the club, the FIDE ID, the year of birth, and the K-factor for
 * the FIDE list the group is rated on (`settings`, the group's: see nsfRatedList). The
 * member is what the server sent, which already leaves out what the member keeps from
 * being published and every rating.
 */
export function nsfChanges(player, member, settings) {
    const p = player || {};
    const m = member || {};
    const out = [];
    const add = (field, from, to) => { if (to && from !== to) out.push({ field, from, to }); };
    add('nsfId', text(p.nsfId), text(m.nsfId));
    // A club is the same club in any case ("dragen", "Dragen").
    if (text(m.club) && text(p.club).toLowerCase() !== text(m.club).toLowerCase()) out.push({ field: 'club', from: text(p.club), to: text(m.club) });
    add('fideId', text(p.fideId), text(m.fideId));
    add('birthYear', playerYear(p), text(m.birthYear));
    add('kFactor', text(p.kFactor), nsfKFactor(m, settings));
    return out;
}

/**
 * The fields to write onto a player for a set of changes. The year of birth goes to
 * `birthYear`, which every reader of a roster uses; a full date of birth, if there is one,
 * is the arbiter's to correct by hand.
 */
export function nsfChangesAsFields(changes) {
    const fields = {};
    (changes || []).forEach(c => { fields[c.field] = c.to; });
    return fields;
}

/**
 * Counts for the Statistics window, over the players in scope:
 *   members    paid for the tournament's year (certain matches)
 *   fide       not paid-up members, but in an NGP group on a FIDE ID
 *   lapsed     found, paid for an earlier year only
 *   toCheck    probable, several to choose from, or an NSF-ID that belongs to someone else
 *   notFound   not among NSF's paid members
 *   unchecked  added since the last check
 *
 * @param {Array<object|null>} results  one per player: the server's result — or what was
 *   found when they registered (nsfResultFromRegistration) — or null
 */
export function nsfSummary(results) {
    const s = { total: 0, members: 0, fide: 0, lapsed: 0, toCheck: 0, notFound: 0, unchecked: 0 };
    (results || []).forEach(r => {
        s.total++;
        if (!r) { s.unchecked++; return; }
        if (r.status === 'unique' && r.membership === 'active') s.members++;
        else if (r.fide) s.fide++;
        else if (r.status === 'unique' && r.membership === 'lapsed') s.lapsed++;
        else if (r.status === 'none') s.notFound++;
        else s.toCheck++;
    });
    return s;
}
